<script lang="ts">
	import type { Route, RouteVignettes } from '$lib/components/map/types';
	import type { BspAutoTrackingName } from '$lib/services/tracking/googleAnalytics/events';
	import AlertBox from '$lib/components/atoms/alertBox/AlertBox.svelte';
	import ObfuscatedLink from '$lib/components/atoms/obfuscatedLink/ObfuscatedLink.svelte';
	import { type TripOptionCar, tripOptionsCarName } from '$lib/components/itinerary/tripOptions';
	import { getLanguageCode } from '$lib/i18n/language';
	import { formatPrice } from '$lib/utils/formatPrice';
	import * as m from '$paraglide/messages';
	import { onMount } from 'svelte';
	import HorizontalStackedBarChart from './HorizontalStackedBarChart.svelte';

	interface Props {
		costs: Route['costs'];
		tolls: Route['tolls'];
		totalCost: Route['totalCost'];
		vignettes: RouteVignettes;
		car: TripOptionCar | undefined;
		text?: string;
		ctaText?: string;
		ctaLink?: string;
		color?: string;
		trackingName?: BspAutoTrackingName | undefined;
		onEditCar?: () => void;
		onAdTextLineCtaClick?: (trackingName: BspAutoTrackingName) => void;
		onMountAdTextLine?: (trackingName: BspAutoTrackingName) => void;
	}

	const {
		costs,
		tolls,
		totalCost,
		car,
		text = '',
		ctaText = '',
		ctaLink = '',
		color = '',
		vignettes,
		trackingName,
		onEditCar,
		onAdTextLineCtaClick,
		onMountAdTextLine,
	}: Props = $props();

	const costKeys = ['fuel', 'electricity', 'tolls', 'vignette'] as const;
	type CostKey = (typeof costKeys)[number];

	const costBackgrounds: Record<CostKey, string> = {
		fuel: 'bg-quick-weak',
		electricity: 'bg-electric-weak',
		tolls: 'bg-economic-weak',
		vignette: 'bg-ecologic-weak',
	};

	const costLabels: Record<CostKey, string> = {
		fuel: m.route_details__costs_section__fuel(),
		electricity: m.route_details__costs_section__electricity(),
		tolls: m.route_details__costs_section__tolls(),
		vignette: m.route_details__costs_section__vignette(),
	};

	onMount(() => {
		if (!trackingName) return;
		onMountAdTextLine?.(trackingName);
	});

	const datasets = $derived.by(() => {
		const adsTextLine = text && ctaText && ctaLink && trackingName
			? [{
				label: text,
				nb: 0,
				color,
				withCustomDescription: true,
				metadata: { adsTextLine: { ctaLink, ctaText, trackingName } },
			}]
			: [];

		if (!costs || !Object.values(costs).some(cost => cost?.amount)) {
			return adsTextLine;
		}

		return [
			...costKeys
				.filter(key => costs[key].amount)
				.map(key => ({
					label: costLabels[key],
					nb: costs[key].amount,
					description: formatPrice({
						amount: costs[key].amount,
						currency: costs[key].currency,
						language: getLanguageCode(),
						digits: 2,
					}),
					colorClass: costBackgrounds[key],
					details: key === 'vignette'
						? vignettes?.map(detail => ({
							message: detail?.message,
							name: detail?.name,
							cost: detail?.cost && formatPrice({
								amount: detail?.cost.amount ?? 0,
								currency: detail?.cost.currency ?? '',
								language: getLanguageCode(),
								digits: 2,
							}),
						}))
						: undefined,
				})),
			...adsTextLine,
		];
	});
</script>

<div class="grid gap-2 md:w-full" data-testid="cost-list">
	{#if car}
		<AlertBox
			title={m.route_details__costs_section__estimated_for()}
			text={tripOptionsCarName.serialize(car)}
			buttonIcon="tune"
			buttonText={m.action__edit()}
			onclick={onEditCar}
			color={car.isDefault ? 'amber' : 'white'}
			class="w-full"
		/>
	{/if}

	{#if totalCost.amount > 0}
		<div class="grid gap-2 rd-lg bg-white shadow-smooth-block p-2">
			<div class="text-heading-semibold mb-3 flex justify-between">
				{m.route_details__costs_section__costs_details()}
				<span>
					{formatPrice({
						amount: totalCost.amount,
						currency: totalCost.currency,
						language: getLanguageCode(),
						digits: 2,
					})}
				</span>
			</div>
			<HorizontalStackedBarChart {datasets} showBarChart>
				{#snippet description({ dataset })}
					<div
						class="text-[var(--color)]"
						style="--color: {dataset.color}"
					>
						{#if dataset.metadata?.adsTextLine}
							{@const adsTextLine = dataset.metadata.adsTextLine}
							<ObfuscatedLink
								href={adsTextLine.ctaLink}
								target="_blank"
								class="cursor-pointer font-semibold gap-x-0.5 block"
								onclick={() => {
									onAdTextLineCtaClick?.(adsTextLine.trackingName);
								}}
							>
								<span>{adsTextLine.ctaText}</span>
							</ObfuscatedLink>
						{/if}
					</div>
				{/snippet}
			</HorizontalStackedBarChart>
		</div>
	{/if}

	{#if tolls.length}
		<div class="grid gap-2 text-md rd-lg bg-white shadow-smooth-block p-2">
			<div class="text-heading-semibold mb-3">
				{m.route_details__costs_section__tolls_list()}
			</div>
			<div class="grid gap-2">
				<!-- eslint-disable-next-line svelte/require-each-key -->
				{#each tolls as toll}
					<div class="flex justify-between items-center text-coloredneutral">
						<div>{toll.name}</div>
						<div class="font-semibold text-coloredneutral mb-1.5">
							{formatPrice({
								amount: toll.cost.amount,
								currency: toll.cost.currency,
								language: getLanguageCode(),
								digits: 2,
							})}
						</div>
					</div>
				{/each}
			</div>
		</div>
	{/if}
</div>
